import { useEffect, useRef, useState } from 'react';
import * as tus from 'tus-js-client';
import { adminApi, type VideoInfo } from './api';
import { formatBytes, formatDuration, uploadText } from './upload-texts';
import { formatClock } from '../format';

/**
 * Video-Upload für eine Lektion (aus Evercast): Vorprüfung im Browser (Größe, Endung, Auflösung, Laufzeit),
 * dann tus – fortsetzbar in Paketen. Danach zeigt die Karte die Verarbeitung, bis das Video fertig ist.
 */

const LIMITS = { maxBytes: 8 * 1024 ** 3, maxWidth: 4096, maxSeconds: 180 * 60, shortSeconds: 60 };
const EXTENSIONS = ['.mp4', '.mov', '.mkv', '.m4v'];

interface Precheck {
  errors: { key: string; vars: Record<string, string | number> }[];
  warnings: { key: string; vars: Record<string, string | number> }[];
}

async function precheck(file: File): Promise<Precheck> {
  const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  const format = ext.replace('.', '').toUpperCase() || 'unbekannt';
  const result: Precheck = { errors: [], warnings: [] };
  if (!EXTENSIONS.includes(ext)) {
    result.errors.push({ key: 'upload.error.container_unsupported', vars: { format } });
    return result;
  }
  if (file.size > LIMITS.maxBytes) result.errors.push({ key: 'upload.error.file_too_large', vars: { groesse: formatBytes(file.size) } });
  const meta = await readMetadata(file);
  if (!meta) return result;
  const { width, height, duration } = meta;
  const dauer = formatDuration(duration);
  if (width > LIMITS.maxWidth) result.errors.push({ key: 'upload.error.width_exceeded', vars: { breite: width } });
  if (duration > LIMITS.maxSeconds) result.errors.push({ key: 'upload.error.duration_too_long', vars: { dauer } });
  if (result.errors.length) return result;
  if (height > 1080) result.warnings.push({ key: 'upload.warn.resolution_too_high', vars: { breite: width, hoehe: height, faktor: Math.max(2, Math.round((width * height) / (1920 * 1080))) } });
  else if (height < 720) result.warnings.push({ key: 'upload.warn.resolution_too_low', vars: { breite: width, hoehe: height } });
  if (Math.abs(width / height - 16 / 9) > 0.02) result.warnings.push({ key: 'upload.warn.aspect_ratio', vars: { breite: width, hoehe: height } });
  if (duration > 0 && duration < LIMITS.shortSeconds) result.warnings.push({ key: 'upload.warn.duration_very_short', vars: { dauer } });
  return result;
}

function readMetadata(file: File): Promise<{ width: number; height: number; duration: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    const finish = (value: { width: number; height: number; duration: number } | null) => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), 8000);
    video.onloadedmetadata = () => finish(video.videoWidth && video.videoHeight ? { width: video.videoWidth, height: video.videoHeight, duration: Number.isFinite(video.duration) ? video.duration : 0 } : null);
    video.onerror = () => finish(null);
    video.src = url;
  });
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'blocked'; precheck: Precheck }
  | { kind: 'confirm'; precheck: Precheck; file: File }
  | { kind: 'uploading'; percent: number; remaining: string | null; interrupted: boolean }
  | { kind: 'uploaded' }
  | { kind: 'error'; message: string };

export function VideoUpload({ lessonId, video, onChanged }: { lessonId: string; video: VideoInfo | null; onChanged: () => void }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<tus.Upload | null>(null);

  useEffect(() => () => void uploadRef.current?.abort(), []);

  // Solange verarbeitet wird, alle fünf Sekunden nachsehen.
  useEffect(() => {
    if (!video || !['queued', 'probing', 'transcoding'].includes(video.status)) return;
    const timer = setInterval(() => void adminApi.getVideo(video.id).then((v) => v.status !== video.status || v.progressPercent !== video.progressPercent ? onChanged() : undefined).catch(() => undefined), 5000);
    return () => clearInterval(timer);
  }, [video, onChanged]);

  async function chooseFile(file: File) {
    setPhase({ kind: 'checking' });
    const result = await precheck(file);
    if (result.errors.length) return setPhase({ kind: 'blocked', precheck: result });
    if (result.warnings.length || video) return setPhase({ kind: 'confirm', precheck: result, file });
    start(file);
  }

  function start(file: File) {
    let baseline: { at: number; bytes: number } | null = null;
    const upload = new tus.Upload(file, {
      endpoint: '/api/admin/uploads',
      chunkSize: 8 * 1024 * 1024,
      retryDelays: [0, 1000, 3000, 5000, 10000, 20000, 30000],
      metadata: { filename: file.name, filetype: file.type, lessonId },
      headers: { 'X-Requested-With': 'fetch' },
      removeFingerprintOnSuccess: true,
      onShouldRetry: () => {
        setPhase((p) => (p.kind === 'uploading' ? { ...p, interrupted: true } : p));
        return true;
      },
      onProgress: (sent, total) => {
        baseline ??= { at: Date.now(), bytes: sent };
        const elapsed = (Date.now() - baseline.at) / 1000;
        const rate = elapsed > 2 ? (sent - baseline.bytes) / elapsed : 0;
        setPhase({ kind: 'uploading', percent: Math.floor((sent / total) * 100), remaining: rate > 0 ? formatDuration((total - sent) / rate) : null, interrupted: false });
      },
      onSuccess: () => {
        setPhase({ kind: 'uploaded' });
        onChanged();
      },
      onError: (error) => {
        const message = /container_unsupported/.test(String(error)) ? uploadText('upload.error.container_unsupported', { format: file.name.split('.').pop() ?? '' }).title : 'Der Upload ist fehlgeschlagen. Bitte versuch es noch einmal – es geht dort weiter, wo es aufgehört hat.';
        setPhase({ kind: 'error', message });
      },
    });
    uploadRef.current = upload;
    setPhase({ kind: 'uploading', percent: 0, remaining: null, interrupted: false });
    void upload.findPreviousUploads().then((previous) => {
      const p = previous[0];
      if (p) upload.resumeFromPreviousUpload(p);
      upload.start();
    });
  }

  const fileInput = <input ref={inputRef} type="file" accept=".mp4,.mov,.mkv,.m4v,video/mp4,video/quicktime,video/x-matroska" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void chooseFile(f); }} />;

  if (phase.kind === 'uploading') {
    const t = phase.interrupted ? uploadText('upload.paused_connection') : uploadText('upload.progress', { prozent: phase.percent, restzeit: phase.remaining ?? '…' });
    return (
      <div className="upload">
        <div className="progress" role="progressbar" aria-valuenow={phase.percent} aria-valuemin={0} aria-valuemax={100}>
          <div style={{ width: `${phase.percent}%` }} />
        </div>
        <p><strong>{t.title}</strong></p>
        {t.text && <p className="muted small">{t.text}</p>}
      </div>
    );
  }

  if (phase.kind === 'blocked' || phase.kind === 'confirm') {
    const items = phase.kind === 'blocked' ? phase.precheck.errors : phase.precheck.warnings;
    return (
      <div className="upload">
        {fileInput}
        {items.map((i) => {
          const t = uploadText(i.key, i.vars);
          return (
            <div key={i.key} className={t.severity === 'error' ? 'error' : 'warning'}>
              <strong>{t.title}</strong>
              {t.text && <div style={{ marginTop: 4 }}>{t.text}</div>}
            </div>
          );
        })}
        {phase.kind === 'confirm' && video && (
          <div className="warning">
            <strong>Du ersetzt das Video dieser Lektion.</strong>
            <div style={{ marginTop: 4 }}>Das bisherige Video wird abgelöst, die Laufzeit übernimmt das neue. Sprungmarken hinter dem neuen Ende werden markiert.</div>
          </div>
        )}
        <div className="row-actions">
          <button type="button" className="button button--secondary button--small" onClick={() => setPhase({ kind: 'idle' })}>Abbrechen</button>
          {phase.kind === 'confirm' && <button type="button" className="button button--small" onClick={() => start(phase.file)}>Trotzdem hochladen</button>}
          {phase.kind === 'blocked' && <button type="button" className="button button--small" onClick={() => inputRef.current?.click()}>Andere Datei wählen</button>}
        </div>
      </div>
    );
  }

  const status = video ? videoStatus(video) : null;
  const requirements = uploadText('upload.hint.requirements');
  return (
    <div className="upload">
      {fileInput}
      {phase.kind === 'uploaded' && <div className="hint"><strong>{uploadText('upload.complete').title}</strong> {uploadText('upload.complete').text}</div>}
      {status && (
        <div className={status.severity === 'error' ? 'error' : status.severity === 'warn' ? 'warning' : 'hint'}>
          <strong>{status.title}</strong>
          {status.text && <div style={{ marginTop: 4 }}>{status.text}</div>}
          {video && ['queued', 'probing', 'transcoding'].includes(video.status) && (
            <div className="progress" style={{ marginTop: 8 }}><div style={{ width: `${video.progressPercent}%` }} /></div>
          )}
        </div>
      )}
      {video?.warnings.map((w) => {
        const t = uploadText(w, { dauer: formatClock(video.durationSeconds ?? 0), breite: video.width ?? '?', hoehe: video.height ?? '?', faktor: Math.max(2, Math.round(((video.width ?? 0) * (video.height ?? 0)) / (1920 * 1080))) });
        return (
          <div key={w} className="warning">
            <strong>{t.title}</strong>
            {t.text && <div style={{ marginTop: 4 }}>{t.text}</div>}
          </div>
        );
      })}
      {phase.kind === 'error' && <div className="error">{phase.message}</div>}
      <div className="row-actions">
        <button type="button" className="button" onClick={() => inputRef.current?.click()} disabled={phase.kind === 'checking'}>
          {phase.kind === 'checking' ? 'Datei wird geprüft …' : video ? 'Anderes Video hochladen' : 'Video hochladen'}
        </button>
        {video && (
          <button type="button" className="link-button" onClick={() => { if (window.confirm('Video von dieser Lektion lösen? Die Datei bleibt erhalten.')) void adminApi.detachVideo(lessonId).then(onChanged); }}>
            Video entfernen
          </button>
        )}
      </div>
      <p className="muted small">{uploadText('upload.hint.resumable').title}</p>
      <details className="upload__requirements">
        <summary>{requirements.title}</summary>
        <p className="muted small">{requirements.text}</p>
      </details>
    </div>
  );
}

function videoStatus(v: VideoInfo): { severity: 'hint' | 'warn' | 'error'; title: string; text?: string } {
  switch (v.status) {
    case 'ready': {
      const t = uploadText('upload.processing.done', { breite: v.width ?? '?', hoehe: v.height ?? '?', dauer: formatClock(v.durationSeconds ?? 0) });
      return { severity: 'hint', title: t.title, ...(t.text ? { text: t.text } : {}) };
    }
    case 'rejected': {
      const t = uploadText(v.rejectionCode ?? 'upload.error.unreadable');
      return { severity: 'error', title: t.title, ...(t.text ? { text: t.text } : {}) };
    }
    case 'failed': {
      const t = uploadText('upload.processing.failed');
      return { severity: 'error', title: t.title, ...(t.text ? { text: t.text } : {}) };
    }
    default: {
      const t = uploadText('upload.processing', { prozent: v.progressPercent });
      return { severity: 'hint', title: v.status === 'queued' ? 'Dein Video wartet auf die Verarbeitung …' : t.title, ...(t.text ? { text: t.text } : {}) };
    }
  }
}
